/**
 * App Top Bar Context
 * 
 * Allows child pages to customize the global topbar content.
 * Pages can set breadcrumbs, center content, and right actions.
 */
import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface TopBarConfig {
    breadcrumbs?: ReactNode;
    centerContent?: ReactNode;
    rightActions?: ReactNode;
}

interface TopBarContextType {
    config: TopBarConfig;
    setConfig: (config: TopBarConfig) => void;
    clearConfig: () => void;
}

const TopBarContext = createContext<TopBarContextType | undefined>(undefined);

export const TopBarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [config, setConfigState] = useState<TopBarConfig>({});

    const setConfig = useCallback((newConfig: TopBarConfig) => {
        setConfigState(newConfig);
    }, []);

    const clearConfig = useCallback(() => {
        setConfigState({});
    }, []);

    return (
        <TopBarContext.Provider value={{ config, setConfig, clearConfig }}>
            {children}
        </TopBarContext.Provider>
    );
};

export const useTopBar = () => {
    const context = useContext(TopBarContext);
    if (!context) {
        throw new Error('useTopBar must be used within a TopBarProvider');
    }
    return context;
};

/**
 * Hook to set topbar config on mount and clear on unmount
 */
export const useTopBarConfig = (config: TopBarConfig) => {
    const { setConfig, clearConfig } = useTopBar();

    React.useEffect(() => {
        setConfig(config);
        return () => clearConfig();
    }, [config, setConfig, clearConfig]);
};

export default TopBarContext;
